import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import ConsultationModal from "../components/ConsultationModal";
import {
  FaTrophy,
  FaShieldAlt,
  FaFileContract,
  FaSyncAlt,
  FaAward,
  FaHeadset,
  FaLinkedin,
  FaUsers,
  FaPhoneAlt,
  FaEnvelope,
  FaGlobe,
  FaFacebookF,
  FaInstagram,
  FaPaperPlane,
  FaMapMarkerAlt,
} from "react-icons/fa";
import ContactPage from "../pages/ContactPage";
import styles from "../styles_about/AboutTrustSection.module.css";

const trustItems = [
  {
    title: "Proven Experience",
    description:
      "Delivered multiple successful government & enterprise projects",
    icon: FaTrophy,
  },
  {
    title: "Secure & Confidential",
    description: "NDA, data security and strict confidentiality practices",
    icon: FaShieldAlt,
  },
  {
    title: "Source Code Ownership",
    description: "100% ownership of source code & documentation",
    icon: FaFileContract,
  },
  {
    title: "Agile Approach",
    description: "Iterative delivery with regular updates and transparency",
    icon: FaSyncAlt,
  },
  {
    title: "Quality Assurance",
    description: "Robust testing and deployment for reliable solutions",
    icon: FaAward,
  },
  {
    title: "Long-term Support",
    description: "Continuous maintenance and support after go-live",
    icon: FaHeadset,
  },
];

const leadership = [
  {
    name: "Savita Charawande",
    role: "Founder & CEO",
    image: "/photo-2.png",
    description:
      "Visionary leader with deep expertise in software architecture and government digital solutions.",
  },
  {
    name: "Rahul Charvande",
    role: "Co-Founder & CTO",
    image: "/photo-1.png",
    description:
      "Full stack expert focused on building scalable, secure and high-performance systems.",
  },
  {
    name: "Amey Amle",
    role: "Project Director",
    image: "/photo-3.png",
    description:
      "Experienced in project delivery, team management and client relationship.",
  },
];

const statistics = [
  {
    value: "50+",
    label: "Projects Delivered",
  },
  {
    value: "20+",
    label: "Government Clients",
  },
  {
    value: "10+",
    label: "Enterprise Clients",
  },
  {
    value: "100%",
    label: "Client Satisfaction",
  },
];

export default function AboutTrustSection() {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

  return (
    <section className={styles.aboutTrustSection}>
      <div className={styles.sectionContainer}>
        {/* Why Clients Trust Us */}
        <div className={styles.sectionHeading}>
          <h2>Why Clients Trust Us</h2>

          <div className={styles.headingLine}>
            <span />
          </div>
        </div>

        <div className={styles.trustGrid}>
          {trustItems.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={item.title}
                className={styles.trustCard}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.07,
                }}
                whileHover={{
                  y: -7,
                }}
              >
                <div className={styles.trustIcon}>
                  <Icon />
                </div>

                <h3>{item.title}</h3>

                <p>{item.description}</p>
              </motion.article>
            );
          })}
        </div>

        {/* Leadership */}
        <div className={`${styles.sectionHeading} ${styles.leadershipHeading}`}>
          <h2>Our Leadership</h2>

          <div className={styles.headingLine}>
            <span />
          </div>
        </div>

        <div className={styles.leadershipGrid}>
          {leadership.map((member, index) => (
            <motion.article
              key={member.name}
              className={styles.leadershipCard}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.55,
                delay: index * 0.1,
              }}
              whileHover={{
                y: -5,
              }}
            >
              <div className={styles.memberImageWrapper}>
                <img
                  src={member.image}
                  alt={`${member.name} - ${member.role}`}
                  className={styles.memberImage}
                  loading="lazy"
                />
              </div>

              <div className={styles.memberContent}>
                <div className={styles.memberHeader}>
                  <div>
                    <h3>{member.name}</h3>
                    <h4>{member.role}</h4>
                  </div>

                  <button
                    type="button"
                    className={styles.linkedinButton}
                    aria-label={`${member.name} LinkedIn profile`}
                  >
                    <FaLinkedin />
                  </button>
                </div>

                <p>{member.description}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className={styles.teamButtonWrapper}>
          <Link to="/contact" className={styles.teamButton}>
            Meet The Team
            <FaUsers />
          </Link>
        </div>

        {/* Statistics */}
        <motion.div
          className={styles.statisticsBar}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.65 }}
        >
          {statistics.map((statistic, index) => (
            <div className={styles.statisticItem} key={statistic.label}>
              <strong>{statistic.value}</strong>
              <span>{statistic.label}</span>

              {index !== statistics.length - 1 && (
                <div className={styles.statisticDivider} />
              )}
            </div>
          ))}
        </motion.div>

        {/* Office and contact section */}
        <div className={styles.contactGrid}>
          <motion.article
            className={styles.officeCard}
            initial={{ opacity: 0, x: -25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Office</h2>

            <div className={styles.officeAddress}>
              <strong>VishvaConnect Technologies LLP</strong>
              <span>Ahilyanagar, Maharashtra, India</span>
            </div>

            <div className={styles.officeDetails}>
              <a href="tel:+919325906604">
                <span className={styles.contactIcon}>
                  <FaPhoneAlt />
                </span>
                +91 9325906604
              </a>

              <a href="mailto:info@vishvaconnect.com">
                <span className={styles.contactIcon}>
                  <FaEnvelope />
                </span>
                info@vishvaconnect.com
              </a>

              <a
                href="https://vishvaconnect.com"
                target="_blank"
                rel="noreferrer"
              >
                <span className={styles.contactIcon}>
                  <FaGlobe />
                </span>
                https://vishvaconnect.com
              </a>
            </div>

            <div className={styles.socialLinks}>
              <button type="button" aria-label="LinkedIn">
                <FaLinkedin />
              </button>

              <button type="button" aria-label="Facebook">
                <FaFacebookF />
              </button>

              <button type="button" aria-label="Instagram">
                <FaInstagram />
              </button>
            </div>
          </motion.article>

          <motion.div
            className={styles.mapCard}
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.08 }}
          >
            <iframe
              title="VishvaConnect Technologies LLP office location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=74.178937%2C19.582563%2C74.218937%2C19.622563&layer=mapnik&marker=19.602563%2C74.198937"
              loading="lazy"
            />
          </motion.div>

          <motion.article
            className={styles.projectCard}
            initial={{ opacity: 0, x: 25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.14 }}
          >
            <div className={styles.projectGlow} />

            <div className={styles.projectContent}>
              <h2>
                Let&apos;s Build Something Great
                <br />
                Together
              </h2>

              <p>
                Have a project in mind? Let&apos;s discuss how we can help you
                achieve your goals.
              </p>

              <button
                type="button"
                className={styles.projectButton}
                onClick={() => setIsConsultationModalOpen(true)}
              >
                Discuss Your Project
                <FaPaperPlane />
              </button>
            </div>
          </motion.article>
        </div>
      </div>
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
      />
    </section>
  );
}
